<template>
  <div>
    <mobialis-form-with-validation
      @onValidate="submit"
      data-cy="form"
      v-av-analytics-form-submit="{ elementText: $t('message.credit.viewAutoAffordability') }"
    >
      <h3>{{ $t('message.credit.application.autoLoans.title', { vehicle: myVehicle }) }}</h3>
      <p v-html="$t('message.credit.application.autoLoans.subTitle')"></p>
      <mobialis-radio-button
        :data-av-analytics-element-text-value="$t('message.credit.application.autoLoans.loanOptionTitle')"
        data-cy="tradeLines"
        :options="radioOptions"
        :label="$t('message.credit.application.autoLoans.loanOptionTitle')"
        v-model="selected"
        rules="required"
      >
        <template v-slot="{ item }">
          <div v-if="item.label.lenderName" class="lender">{{ item.label.lenderName }}</div>
          <div v-if="item.label.paymentAmount" class="payment">
            <span>{{ $n(item.label.paymentAmount, 'currency', langCode) }}</span>
            <span>
              /
              {{
                item.label.paymentFrequencyCode in paymentFrequency
                  ? $t(`message.common.${paymentFrequency[item.label.paymentFrequencyCode]}`)
                  : ''
              }}</span
            >
          </div>
          <div v-if="item.label.originalBalance" class="balance">
            {{ $t('message.credit.application.autoLoans.originalBalance') }}:
            <span>{{ $n(item.label.originalBalance, 'currency', langCode) }}</span>
          </div>
          <div v-if="item.label.noneOfTheAbove" class="none-option mt-1">
            {{ $t('message.credit.application.autoLoans.tradeinNotInList') }}
          </div>
        </template>
      </mobialis-radio-button>
      <mobialis-card v-if="selected === 'none'" class="mt-3">
        <p class="mt-0">{{ $t('message.credit.application.autoLoans.currentBalanceTitle') }}</p>
        <mobialis-text-input-with-validation
          :label="$t('message.credit.application.autoLoans.currentBalance')"
          rules="required"
          :mask-type="langCode === 'fr-CA' ? 'frenchInt' : 'moneyInt'"
          v-model="currentBalance"
          inputmode="numeric"
          data-cy="manualBalance"
          :helper="$t('message.credit.application.autoLoans.currentBalanceHelper')"
        >
        </mobialis-text-input-with-validation>
      </mobialis-card>
      <mobialis-button type="submit" class="my-4">{{ $t('message.credit.viewAutoAffordability') }}</mobialis-button>
      <small v-html="$t('message.credit.application.autoLoans.finePrint')"></small>
    </mobialis-form-with-validation>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import paymentFrequency from '@/utils/enums/prequalPaymentFrequency';

export default {
  name: 'SelectAutoLoan',
  components: {},
  data() {
    return {
      selected: '',
      currentBalance: '',
      paymentFrequency,
    };
  },
  computed: {
    ...mapGetters({
      getYear: 'trade/getYear',
      getMake: 'trade/getMake',
      getModel: 'trade/getModel',
      langCode: 'user/getLanguageCodeForNumericalInternationalization',
      equifaxTradeLines: 'credit/getEquifaxTradeLines',
    }),
    myVehicle() {
      return `${this.getYear} ${this.getMake} ${this.getModel}`;
    },
    radioOptions() {
      const map = this.equifaxTradeLines.map(line => ({
        label: line,
        value: line.id,
      }));

      const noneOption = {
        label: {
          balanceOnTrade: undefined,
          lenderName: undefined,
          paymentAmount: undefined,
          noneOfTheAbove: true,
        },
        value: 'none',
      };

      return [...map, noneOption];
    },
    selectedTradeLine() {
      return this.equifaxTradeLines.find(line => line.id === this.selected);
    },
  },
  methods: {
    async submit(valid) {
      if (!valid) {
        return;
      }

      try {
        this.$store.commit('SET_LOADING', true);
        if (this.selectedTradeLine !== undefined) {
          this.$store.commit('credit/SET_SELECTED_EQUIFAX_TRADE_LINE', this.selectedTradeLine);
        } else {
          this.$store.commit('trade/SET_VEHICLE_BALANCE_OWING', this.currentBalance);
        }
        this.$store.commit('trade/SET_LEAD_TRADEIN_VEHICLE');
        await this.$store.dispatch('lead/updateLead');
        this.$router.push({
          name: `credit-results-${this.baseRoute}`,
        });
      } catch (err) {
        this.$log('Error in Selecting auto loan - Update Lead: ', err);
        this.$router.push({ name: `credit-in-error-${this.baseRoute}` });
      } finally {
        this.$store.commit('SET_LOADING', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

$border-grey: #dae2ea;

::v-deep {
  legend.label {
    padding-bottom: 10px;
    margin-bottom: 16px;
    border-bottom: 1px solid $border-grey;
  }

  fieldset .form-check-wrapper label.form-check-label {
    width: 100%;
    border-radius: 4px;
    border: 1px solid $border-grey;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.06);
    padding: 16px;
    padding-left: calc(16px + 33px);

    &:hover {
      background-color: $primary-light-background;
      color: unset;

      span.check {
        border-color: $border-grey;
      }
    }
  }

  .form-check-wrapper input[type='radio']:checked + label.form-check-label {
    border-color: $primary;
    background-color: $primary-light-background;

    span.check {
      border-color: $primary;
    }
  }

  label span.check {
    margin-left: 16px;
  }

  .lender {
    font-size: 14px;
    font-weight: 700;
    line-height: 17.5px;
  }

  .payment {
    span:first-child {
      font-size: 28px;
      font-weight: 700;
      line-height: 35px;
    }
    span:last-child {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
    }
  }

  .balance {
    color: #4b5863;
    font-size: 14px;
    font-weight: 400;
    line-height: 21px;
    text-transform: capitalize;

    span {
      font-weight: 700;
    }
  }

  .none-option {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
  }

  small {
    font-size: 12px;
  }
}
</style>
